/**
 * Lake Charles Landing Page - Main App Component
 *
 * Sets up React Router with routes for all pages.
 * Uses CityLayout wrapper from @core for consistent header/footer.
 *
 * Lake Charles has additional routes for Champions Dashboard
 * that are not yet available in Houston.
 */

import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { CityLayout } from '@core';
import { Toaster } from '@/components/ui/toaster';
import lakeCharlesConfig from './city.config';

// Core Page imports (from @core or local)
import { LakeCharlesHomePage } from './pages/HomePage';
import { LakeCharlesPrivacyPolicyPage } from './pages/PrivacyPolicyPage';
import { LakeCharlesTermsOfServicePage } from './pages/TermsOfServicePage';
import { LakeCharlesContactPage } from './pages/ContactPage';
import { LakeCharlesVendorsPage } from './pages/VendorsPage';
import { LakeCharlesCityPartnershipPage } from './pages/CityPartnershipPage';
import { LakeCharlesChampionApplicationPage } from './pages/ChampionApplicationPage';

// Lake Charles-specific: Champions Dashboard routes
import { ChampionsDashboardPage } from './pages/champions/DashboardPage';
import { ChampionsFeedbackPage } from './pages/champions/FeedbackPage';
import { ChampionsDemosPage } from './pages/champions/DemosPage';
import { ChampionsVotingPage } from './pages/champions/VotingPage';
import { ChampionsDirectoryPage } from './pages/champions/DirectoryPage';
import { ChampionsMessagingPage } from './pages/champions/MessagesPage';
import { ChampionsLaunchPage } from './pages/champions/LaunchPage';

// Lake Charles-specific: Admin routes
import { ChampionReviewPage } from './pages/admin/ChampionReviewPage';

// Lake Charles-specific: Governance routes (optional)
// import { VoterProfilePage } from './pages/governance/VoterProfilePage';
// import { CouncilOverviewPage } from './pages/governance/CouncilOverviewPage';

function App() {
  const scrollToForm = () => {
    const formElement = document.getElementById('interest-form');
    if (formElement) {
      formElement.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const navLinks = [
    { label: 'Features', href: '/#features' },
    { label: 'How It Works', href: '/#how-it-works' },
    { label: 'Pricing', href: '/#pricing' },
    { label: 'Vendors', href: '/vendors' },
    { label: 'City Officials', href: '/city-partnership' },
    { label: 'Champions', href: '/#champions' },
  ];

  const footerLinks = [
    { label: 'Terms of Service', href: '/terms' },
    { label: 'Privacy Policy', href: '/privacy' },
    { label: 'Contact Us', href: '/contact' },
  ];

  const socialLinks = [
    {
      platform: 'facebook' as const,
      href: 'https://www.facebook.com/groups/lakecharles.communityapp',
      label: 'Facebook Group',
    },
    {
      platform: 'instagram' as const,
      href: 'https://www.instagram.com/lakecharles.communityapp/',
      label: 'Instagram',
    },
  ];

  return (
    <Router>
      <Toaster />
      <CityLayout
        cityName={lakeCharlesConfig.identity.cityName}
        stateName={lakeCharlesConfig.identity.stateName}
        logoSrc="/media_lakecharles/lake-charles-community-app.svg"
        logoAlt="Lake Charles Community App logo"
        navLinks={navLinks}
        footerLinks={footerLinks}
        socialLinks={socialLinks}
        primaryCtaText="Join Waitlist"
        primaryCtaHref="/#interest-form"
        onPrimaryCtaClick={scrollToForm}
        enableWaitlistDialog={true}
        vendorsPagePath="/vendors"
      >
        <Routes>
          {/* Core routes (matches Houston pattern) */}
          <Route path="/" element={<LakeCharlesHomePage />} />
          <Route path="/privacy" element={<LakeCharlesPrivacyPolicyPage />} />
          <Route path="/terms" element={<LakeCharlesTermsOfServicePage />} />
          <Route path="/contact" element={<LakeCharlesContactPage />} />
          <Route path="/vendors" element={<LakeCharlesVendorsPage />} />
          <Route path="/city-partnership" element={<LakeCharlesCityPartnershipPage />} />
          <Route path="/champions/apply" element={<LakeCharlesChampionApplicationPage />} />

          {/* Legacy route support (redirect old URLs) */}
          <Route path="/apply-champion" element={<LakeCharlesChampionApplicationPage />} />

          {/* Lake Charles-specific: Champions Dashboard */}
          <Route path="/champions/dashboard" element={<ChampionsDashboardPage />} />
          <Route path="/champions/dashboard/feedback" element={<ChampionsFeedbackPage />} />
          <Route path="/champions/dashboard/demos" element={<ChampionsDemosPage />} />
          <Route path="/champions/dashboard/voting" element={<ChampionsVotingPage />} />
          <Route path="/champions/dashboard/directory" element={<ChampionsDirectoryPage />} />
          <Route path="/champions/dashboard/messages" element={<ChampionsMessagingPage />} />
          <Route path="/champions/dashboard/launch" element={<ChampionsLaunchPage />} />

          {/* Lake Charles-specific: Admin routes */}
          <Route path="/admin/champion-review" element={<ChampionReviewPage />} />

          {/* Catch-all: redirect to home */}
          <Route path="*" element={<LakeCharlesHomePage />} />
        </Routes>
      </CityLayout>
    </Router>
  );
}

export default App;
